import { Box, Container, Grid, Typography, Stack, Button, Paper } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

import icMulticanal from "../assets/multicanal.png";
import icAutomacao from "../assets/automacao.png";
import icConversas from "../assets/conversas.png";
import icEvolucao from "../assets/evolucao.png";
import icGuardrail from "../assets/guardrail.png";

import chatgptIcon from "../assets/chatgpt-icon.png";
import metaIcon from "../assets/meta-icon.png";
import geminiIcon from "../assets/gemini-icon.png";
import nvidiaIcon from "../assets/nvidia-icon.png";

const defaultItems = [
  { img: icMulticanal, label: "Atuação multicanal" },
  { img: icAutomacao, label: "Automação personalizada" },
  { img: icConversas, label: "Conversas inteligentes" },
  { img: icEvolucao, label: "Evolução constante" },
  { img: icGuardrail, label: "Mecanismo GuardRail" },
];

export default function About() {
  const { t } = useTranslation();

  const offerList = t("about.offerList", { returnObjects: true, defaultValue: defaultItems.map(i => i.label) });

  const items = defaultItems.map((it, idx) => ({ ...it, label: offerList[idx] || it.label }));

  const splitTwoLines = (text = "") => {
    const parts = text.split(" ");
    if (parts.length <= 1) return [text, ""];
    const mid = Math.ceil(parts.length / 2);
    return [parts.slice(0, mid).join(" "), parts.slice(mid).join(" ")];
  };

  return (
    <>
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#fff",
          color: "#111",
        }}
      >
        <Container>
          <Box sx={{ width: 1248, maxWidth: "calc(100% - 32px)", mx: "auto", height: { md: 420 } }}>
            <Box
              sx={{
                display: { xs: "block", md: "grid" },
                gridTemplateColumns: { md: "624px 624px" },
                columnGap: { md: 0 },
                rowGap: { xs: 4, md: 0 },
                justifyContent: "center",
                alignItems: "start",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  width: { md: 624, xs: "100%" },
                  height: { md: 384 },
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    lineHeight: 1.05,
                    fontSize: { xs: 44, md: 72, lg: 88 },
                    mb: { xs: 4, lg: 0 },
                    m: 0,
                    whiteSpace: { md: "pre-line" },
                  }}
                >
                  <Trans components={{ bold: <Box key="bold" component="strong" sx={{ fontWeight: 800 }} /> }}>{t("about.howTitle")}</Trans>
                </Typography>
              </Box>

              <Box
                elevation={0}
                sx={{
                  width: { md: 624, xs: "100%" },
                  height: { md: 420 },
                  borderRadius: 2.5,
                  backgroundColor: "#00FF4F",
                  color: "#0b0c0b",
                  p: { xs: 3, md: 4 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  boxShadow: "none",
                  transition: "box-shadow .25s ease, transform .25s ease",
                  "&:hover": {
                    boxShadow: (theme) => theme.shadows[6],
                  },
                }}
              >
                <Typography sx={{ fontWeight: 800, mb: 2, fontSize: { xs: 18, md: 30 } }}>
                  {t("about.howLead")}
                </Typography>

                <Typography sx={{ opacity: 0.9, mb: 3, fontSize: { xs: 14, md: 18 } }}>
                  {t("about.howBody")}
                </Typography>

                <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, md: 30 } }}>
                  {t("about.offerTitle")}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Grid
            container
            spacing={{ xs: 3, md: 16 }}
            sx={{ mt: { xs: 5, md: 7 }, mb: { xs: 4, md: 6 } }}
            justifyContent="center"
          >
            {items.map((it, idx) => {
              const [l1, l2] = splitTwoLines(it.label);
              return (
                <Grid key={idx} size={{ xs: 5.5, sm: 3.5, md: 1.8 }}>
                  <Stack alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        backgroundColor: "#2d2e2f",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={it.img}
                        alt={it.label}
                        sx={{ width: 75, height: 75 }}
                      />
                    </Box>
                    <Typography
                      align="center"
                      sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 }, lineHeight: 1.25 }}
                    >
                      {l1}
                      {l2 ? (
                        <>
                          <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                            {" "}
                          </Box>
                          <Box component="br" sx={{ display: { xs: "inline", md: "none" } }} />
                          {l2}
                        </>
                      ) : null}
                    </Typography>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ width: 1248, maxWidth: "calc(100% - 32px)", mx: "auto" }}>
            <Button
              href="#contato"
              variant="contained"
              disableElevation
              sx={{
                backgroundColor: "#00FF4F",
                color: "#0b0c0b",
                width: "100%",
                minHeight: { xs: 112, md: 90 },
                borderRadius: 9999,
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": { backgroundColor: "#00e645" },
              }}
            >
              <Box
                sx={{
                  width: { md: 1176, xs: "100%" },
                  maxWidth: { xs: "32ch", md: "none" },
                  mx: { xs: "auto", md: 0 },
                  px: { xs: 2, md: 0 },
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  whiteSpace: { xs: "normal", md: "nowrap" },
                  lineHeight: { xs: 1.25, md: "42px" },
                  fontSize: { md: 28, xs: 18 },
                  fontWeight: 500,
                  overflow: "hidden",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  displayPrint: "block",
                  display: { xs: "-webkit-box", md: "flex" },
                  WebkitLineClamp: { xs: 3, md: "unset" },
                  WebkitBoxOrient: { xs: "vertical", md: "initial" },
                }}
              >
                <Trans
                  components={{
                    bold: (
                      <Box
                        key="bold"
                        component="strong"
                        sx={{
                          fontWeight: 800,
                          display: "inline",
                          mx: { xs: 0, md: 0.5 },
                        }}
                      />
                    ),
                  }}
                >
                  {t("about.ctaMarketing")}
                </Trans>
              </Box>
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ===== BLOCO 2 (faixa verde fluorescente) ===== */}
      <Box
        component="section"
        sx={{
          backgroundColor: "#CEFF1A",
          color: "#0b0c0b",
          py: { xs: 1, md: 2 },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", lg: "1440px" },
            mx: "auto",
            pt: { lg: "56px" },
            pb: { lg: "16px" },
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Título da faixa verde */}
          <Typography
            component="h3"
            sx={{
              textAlign: "center",
              fontFamily: "Poppins",
              fontWeight: 400,
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
              fontSize: {
                xs: "clamp(24px, 6vw, 28px)",
                md: "clamp(32px, 4.2vw, 38px)",
                lg: "clamp(40px, 3.2vw, 48px)",
              },
              maxWidth: 1100,
              mx: "auto",
              mb: { xs: 0, md: 2 },
              mt: { xs: 4, md: 2 },
            }}
          >
            <Trans components={{ bold: <Box key="bold" component="strong" sx={{ fontWeight: 800 }} /> }}>{t("about.greenBandTitle")}</Trans>
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "560px 560px" },
              justifyContent: { lg: "center" },
              columnGap: { lg: "56px" },
              rowGap: { xs: "32px" },
            }}
          >
            {/* Card branco à esquerda */}
            <Paper
              elevation={0}
              sx={{
                width: { lg: "560px" },
                height: { lg: "320px" },
                backgroundColor: "#FFFFFF",
                color: "#000",
                top: 36,
                borderRadius: "24px",
                p: "36px",
                position: "relative",
                overflow: "visible",
                display: "flex",
                flexDirection: "column",
                boxShadow: "none",
                transition: "box-shadow .25s ease, transform .25s ease",
                "&:hover": {
                  boxShadow: (theme) => theme.shadows[6],
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 18, md: 20, lg: 22 },
                  lineHeight: 1.35,
                  mb: "16px",
                }}
              >
                <Trans components={{ bold: <Box key="bold" component="strong" sx={{ fontWeight: 800 }} /> }}>{t("about.consultTitle")}</Trans>
              </Typography>

              <Typography sx={{ fontSize: { xs: 18, md: 20, lg: 22 }, lineHeight: 1.6 }}>
                <Trans components={{ bold: <Box key="bold" component="strong" sx={{ fontWeight: 800 }} /> }}>{t("about.consultBody")}</Trans>
              </Typography>

              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  right: "36px",
                  bottom: "-32px",
                  width: "235px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  columnGap: "13px",
                }}
              >
                {[chatgptIcon, metaIcon, geminiIcon, nvidiaIcon].map((src, i) => (
                  <Box
                    key={i}
                    component="img"
                    alt=""
                    src={src}
                    sx={{
                      width: "50px",
                      height: "50px",
                      display: "block",
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  />
                ))}
              </Box>
            </Paper>

            {/* Texto à direita (3 parágrafos) */}
            <Box sx={{ height: { xs: 28, sm: 36 }, display: { lg: "none" } }} />

            <Box
              sx={{
                width: { lg: "606px" },
                minHeight: { lg: "420px" },
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Stack spacing="16px" sx={{ width: "100%", color: "#000" }}>
                <Typography sx={{ fontSize: { xs: 15, md: 16, lg: 17 }, lineHeight: 1.6 }}>
                  <Trans components={{ bold: <Box key="bold" component="strong" sx={{ fontWeight: 800 }} /> }}>{t("about.leaders")}</Trans>
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}